import {
    TCheckoutcomConfig,
    TCheckoutcomPaymentResponse,
    TCheckoutcomPaymentSession,
    TCheckoutcomSubmitData
} from 'core/checkout/CheckoutcomComponent';

const CHECKOUT_COM_METHOD_ID_PREFIX = 'checkout_com_';

/**
 * Checkout.com components that are digital wallets. Keep in sync with WALLET_COMPONENTS in
 * scripts/checkoutcom/checkoutcomPaymentMethods.js, which picks the widget the accordion item renders with.
 */
const CHECKOUT_COM_WALLET_COMPONENTS = ['applepay'];

/**
 * Callbacks the summary step hands to the wallet button, which it renders in place of Place Order
 */
export type TCheckoutcomWalletHandlers = {
    /** Validates the summary step synchronously, the wallet sheet only opens when it passes */
    validate: () => boolean;
    /** Places the order, resolved with true once it exists and the payment may be submitted */
    placeOrder: () => Promise<boolean>;
    /** Shows an error on the summary step */
    onError: (message: string) => void;
};

/**
 * Whether the device can pay with the wallet. `checking` while Flow is being asked, the item stays hidden until then.
 */
type TWalletAvailability = 'checking' | 'available' | 'unavailable';

/**
 * @description Checks whether an SFCC payment method id belongs to a Checkout.com wallet component
 * @param methodId - SFCC payment method id
 * @returns True for wallet methods such as `checkout_com_applepay`
 */
export function isCheckoutcomWalletMethod(methodId?: string): boolean {
    if (!methodId || methodId.indexOf(CHECKOUT_COM_METHOD_ID_PREFIX) !== 0) {
        return false;
    }

    return CHECKOUT_COM_WALLET_COMPONENTS.includes(methodId.substring(CHECKOUT_COM_METHOD_ID_PREFIX.length));
}

/**
 * @param CheckoutcomComponent Base widget for extending
 * @returns Checkout.com wallet accordion item widget
 */
function CheckoutcomWalletComponentClassCreator(CheckoutcomComponent: import('core/checkout/CheckoutcomComponent').TCheckoutcomComponent) {
    /**
     * @category widgets
     * @subcategory checkout
     * @class CheckoutcomWalletComponent
     * @augments CheckoutcomComponent
     * @classdesc Checkout.com digital wallet (Apple Pay) accordion item. Flow cannot `submit()` a wallet
     * component, the payment sheet only opens from a click on the wallet's own button. So instead of
     * mounting the component in the accordion panel and submitting it after Place Order, the summary
     * step renders the wallet button in place of Place Order and mounts it through `mountWalletButton`.
     *
     * The order is created once the shopper has authorized the payment in the sheet: `handleSubmit`
     * places the order first and then submits the payment session under the order number, exactly as
     * for the other Checkout.com methods. The sheet stays open on its processing state meanwhile.
     */
    class CheckoutcomWalletComponent extends CheckoutcomComponent {
        walletContainer: HTMLElement | null = null;

        walletHandlers: TCheckoutcomWalletHandlers | null = null;

        walletMountRequest: Promise<unknown> | null = null;

        walletAvailability: TWalletAvailability = 'checking';

        /**
         * Set when the accordion asked to open the item while its availability was still being checked, e.g.
         * to reopen the method saved on the basket. The item opens once the wallet turns out to be available.
         */
        isOpenDeferred: boolean = false;

        /**
         * @description Keeps the accordion item hidden until Flow confirms the wallet is available. Flow is the
         * only check: it covers both the device, including browsers without native Apple Pay (desktop Chrome,
         * Firefox, Edge) where it offers a QR code scanned with an iPhone, and whether the payment session
         * includes the wallet at all.
         */
        init() {
            super.init();

            this.hide();
            this.checkWalletAvailability();
        }

        /**
         * @description Asks Flow whether the wallet is available and shows the item if it is
         */
        checkWalletAvailability() {
            this.isWalletAvailable().then(isAvailable => {
                const self = this.ref('self').get() as HTMLElement | undefined;

                if (!self || !self.isConnected) {
                    // the payment methods were re-rendered meanwhile, the new item runs its own check
                    return;
                }

                this.walletAvailability = isAvailable ? 'available' : 'unavailable';

                if (!isAvailable) {
                    return;
                }

                this.show();

                if (this.isOpenDeferred) {
                    this.isOpenDeferred = false;
                    this.openPanel();
                }
            });
        }

        /**
         * @description Keeps a hidden item from being opened. The accordion reopens the method saved on the
         * basket, which can be the wallet when the shopper chose it on another device. While availability is
         * still being checked the request is held until the check completes.
         */
        openPanel() {
            if (this.walletAvailability === 'checking') {
                this.isOpenDeferred = true;

                return;
            }

            if (this.walletAvailability === 'unavailable') {
                return;
            }

            super.openPanel();
        }

        /**
         * @description Drops a deferred open. The accordion closes every item whenever another one is
         * opened, the shopper has picked something else in that case.
         */
        closePanel() {
            this.isOpenDeferred = false;

            super.closePanel();
        }

        /**
         * @description Asks Flow whether the wallet can be offered on this device, without mounting it. The
         * summary step asks again when it mounts the button, against a fresh payment session.
         * @returns Promise resolved with true when Flow offers the wallet
         */
        async isWalletAvailable(): Promise<boolean> {
            const paymentSession = this.getPaymentSession();

            if (!paymentSession || !this.prefs().publicKey) {
                return false;
            }

            try {
                const checkoutWebComponents = await this.loadSdk();

                if (!checkoutWebComponents) {
                    return false;
                }

                const checkout = await checkoutWebComponents({
                    ...this.getCheckoutConfig(paymentSession),
                    onError: (_, error) => {
                        // nothing to report to the shopper, the item just stays hidden
                        // eslint-disable-next-line no-console
                        console.error('Checkout.com wallet availability error:', error);
                    }
                });

                return await checkout.create(this.prefs().componentName).isAvailable();
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Checkout.com wallet availability error:', error);

                return false;
            }
        }

        /**
         * @description The wallet collects nothing on the payment step, so there is nothing to validate there
         * @returns Always true
         */
        isChildrenValid(): boolean {
            this.hideError();

            return true;
        }

        /**
         * @description Mounts the component only into the summary step container. Opening the accordion
         * panel therefore mounts nothing.
         * @returns Mounted wallet component, or null when no summary container was handed over
         */
        initCheckoutcomComponent() {
            if (!this.walletContainer || !this.walletContainer.isConnected) {
                return Promise.resolve(null);
            }

            return super.initCheckoutcomComponent();
        }

        /**
         * @description Returns the selector of the summary step container the wallet button mounts into
         * @returns Container selector or null when missing
         */
        getComponentContainerSelector(): string | null {
            const container = this.walletContainer;

            if (!container) {
                return null;
            }

            if (!container.id) {
                container.id = `checkoutcom-wallet-${this.prefs().methodId || 'payment'}`;
            }

            return `#${container.id}`;
        }

        /**
         * @description Mounts the wallet button into the summary step. Called whenever the summary
         * controls re-render, so mounting again into the container already holding the button is a no-op.
         * The payment session is requested again first, the basket may have changed since it was created.
         * @param container - Element to mount the wallet button into
         * @param handlers - Summary step callbacks
         * @returns Promise resolved once the button was mounted, or could not be
         */
        mountWalletButton(container: HTMLElement, handlers: TCheckoutcomWalletHandlers): Promise<unknown> {
            this.walletHandlers = handlers;

            if (this.walletContainer === container && (this.walletMountRequest || this.paymentComponent)) {
                return this.walletMountRequest || Promise.resolve(this.paymentComponent);
            }

            this.unmountComponent();
            this.walletContainer = container;
            this.sessionStale = true;

            const walletMountRequest = this.initCheckoutcomComponent().then(paymentComponent => {
                if (this.walletMountRequest === walletMountRequest) {
                    this.walletMountRequest = null;
                }

                return paymentComponent;
            });

            this.walletMountRequest = walletMountRequest;

            return walletMountRequest;
        }

        /**
         * @description Adds the wallet click check, and only reports SDK errors raised while the button is
         * being mounted as a load error. Later on the wallet sheet reports its own failures, failed
         * submissions are reported by `handleSubmit`, and a failed order may already have sent the shopper
         * back to the payment step, where a stray load error would show up under this item.
         * @param paymentSession - Payment session the component is mounted against
         * @returns SDK configuration
         */
        getCheckoutConfig(paymentSession: TCheckoutcomPaymentSession): TCheckoutcomConfig {
            const config = super.getCheckoutConfig(paymentSession);

            return {
                ...config,
                handleClick: () =>
                    Promise.resolve({
                        continue: !!this.walletHandlers && this.walletHandlers.validate()
                    }),
                onError: (component, error) => {
                    if (this.walletMountRequest) {
                        config.onError(component, error);

                        return;
                    }

                    // eslint-disable-next-line no-console
                    console.error('Checkout.com wallet error:', error);
                }
            };
        }

        /**
         * @description Places the order before the payment is submitted. The shopper authorized the
         * payment in the wallet sheet, which is the first point at which it is known that they want to pay.
         * @param submitData - Session data token captured by Flow
         * @returns Checkout.com payment response, or nothing to abort the submission
         */
        async handleSubmit(submitData: TCheckoutcomSubmitData): Promise<TCheckoutcomPaymentResponse | void> {
            const isOrderPlaced = this.walletHandlers ? await this.walletHandlers.placeOrder() : false;

            if (!isOrderPlaced) {
                // the summary step has already reported why
                return undefined;
            }

            return super.handleSubmit(submitData);
        }

        /**
         * @description Shows errors on the summary step while the wallet button is mounted there, the
         * accordion item is collapsed at that point
         * @param message - Error message
         */
        setError(message: string) {
            if (this.walletContainer && this.walletHandlers) {
                this.walletHandlers.onError(message);

                return;
            }

            super.setError(message);
        }

        /**
         * @description Releases the summary step container on billing step activation, the shopper has
         * left the summary step and the next mount gets a fresh container
         * @param stage - Checkout stage the cleanup runs for
         */
        cleanupSensitiveData(stage: string) {
            super.cleanupSensitiveData(stage);

            if (stage !== 'activation') {
                return;
            }

            this.walletContainer = null;
            this.walletMountRequest = null;
        }
    }

    return CheckoutcomWalletComponent;
}

export type TCheckoutcomWalletComponent = ReturnType<typeof CheckoutcomWalletComponentClassCreator>;
export type TCheckoutcomWalletComponentInstance = InstanceType<TCheckoutcomWalletComponent>;

export default CheckoutcomWalletComponentClassCreator;
